import { useContext, useState } from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { Grid } from "@mui/material";
import { AlarmEditDetailsHeader } from "./AlarmEditDetailsHeader";
import { EditIconButton } from "../../../main/components/EditIconButton";
import ConfirmationDialog from "./ConfirmationDialog";
import { ConfirmationModal } from "../../../main/components/ConfirmationModal";
import { ActionAlert } from "../../../main/components/alerts/ActionAlert";

import { AlarmDetails } from "../types";
import { alarmsApi } from "../../../services/api";
import BaseButton from "../../../main/components/BaseButton";
import { MainContext } from "../../../main/context/MainContextProvider";
import DangerBaseButton from "../../../main/components/DangerBaseButton";
import { PatchAlarmDetails } from "../../../services/api/routes/alarms";
import TextInput from "../../../main/components/TextInput";
import { DetailsInfo } from "../../clients/components/DetailsInfo";

const schema = yup.object().shape({
  specializedTechnical: yup.string().nullable(),
  technicalAssistance: yup.string().nullable(),
  provider: yup.string().nullable(),
  dealership: yup.string().nullable(),
});

export type EditAlarmDetails = yup.InferType<typeof schema>;

export default function AlarmDetailsEditContent(props: {
  alarm: AlarmDetails | null;
  onMount: () => void;
}) {
  const { alarm, onMount } = props;
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const [isSaveConfirmationOpen, setSaveConfirmationOpen] =
    useState<boolean>(false);
  const [isCancelConfirmationOpen, setCancelConfirmationOpen] =
    useState<boolean>(false);
  const [openSuccessModal, setOpenSuccessModal] = useState<boolean>(false);
  const [openErrorModal, setOpenErrorModal] = useState<boolean>(false);
  const { theme } = useContext(MainContext);
  const primary = theme.muiTheme.palette.primary.main;
  const path = window.location.pathname;
  const alarmCode = path.split("/")[3];
  const [specializedTechnicalEdit] = useState(
    alarm?.actions?.specializedTechnical || ""
  );
  const [technicalAssistanceEdit] = useState(
    alarm?.actions?.technicalAssistance || ""
  );
  const [providerEdit] = useState(alarm?.actions?.provider || "");
  const [dealershipEdit] = useState(alarm?.actions?.dealership || "");

  const formDetailsInitialValue = {
    specializedTechnical: alarm?.actions?.specializedTechnical,
    technicalAssistance: alarm?.actions?.technicalAssistance,
    provider: alarm?.actions?.provider,
    dealership: alarm?.actions?.dealership,
  };

  const {
    handleSubmit,
    control,
    formState: { errors },
  } = useForm({
    defaultValues: formDetailsInitialValue,
    resolver: yupResolver(schema),
  });

  const handleEdit = () => {
    setIsEditing(true);
  };

  const handleSave = () => {
    setSaveConfirmationOpen(true);
  };

  const handleCancel = () => {
    setCancelConfirmationOpen(true);
  };

  const handleConfirmSave = async (data: EditAlarmDetails) => {
    try {
      const updateData: PatchAlarmDetails = {};

      if (
        data.specializedTechnical !==
        formDetailsInitialValue.specializedTechnical
      ) {
        updateData.specializedTechnical = data.specializedTechnical || "";
      }

      if (
        data.technicalAssistance !== formDetailsInitialValue.technicalAssistance
      ) {
        updateData.technicalAssistance = data.technicalAssistance || "";
      }

      if (data.provider !== formDetailsInitialValue.provider) {
        updateData.provider = data.provider || "";
      }

      if (data.dealership !== formDetailsInitialValue.dealership) {
        updateData.dealership = data.dealership || "";
      }

      if (Object.keys(updateData).length > 0) {
        await alarmsApi.patchAlarmDetails(alarmCode, updateData);
      } else {
      }
    } catch (error) {
      setOpenErrorModal(true);
    }
  };

  const onSubmit = () => {
    setSaveConfirmationOpen(true);
  };

  const handleConfirmCancel = () => {
    if (isEditing) {
      setIsEditing(false);
      setCancelConfirmationOpen(false);
    } else {
      setSaveConfirmationOpen(false);
    }
  };

  return (
    <>
      <Grid
        sx={{
          padding: { sm: "15px", xs: "15px" },
          flexDirection: "row",
          display: "flex",
          backgroundColor: "rgba(217, 217, 217, 0.1)",
          borderRadius: 2,
          marginTop: "8px",
          marginBottom: "8px",
          rowGap: "16px ",
        }}
        container
      >
        <Grid item xs={8} md={8}>
          <AlarmEditDetailsHeader title="Detalhes" />
        </Grid>
        <Grid item xs={4}>
          {isEditing ? (
            <>
              <Grid
                item
                container
                flexWrap="nowrap"
                justifyContent="flex-end"
                spacing={2}
                sx={{
                  marginBottom: "15px",
                }}
              >
                <Grid item>
                  <DangerBaseButton
                    onClick={handleCancel}
                    title="Cancelar"
                    sx={{
                      height: "34px",
                      width: "95px",
                    }}
                  />
                </Grid>
                <Grid item>
                  <BaseButton
                    onClick={handleSave}
                    sx={{
                      backGroundColor: primary,
                      borderColor: primary,
                      height: "34px",
                      width: "95px",
                      "& .MuiTypography-root": {
                        color: primary,
                      },
                      "&:hover": {
                        borderColor: primary,
                        backgroundColor: primary,
                        color: primary,
                        "& .MuiTypography-root": {
                          color: "#FFFFFF",
                        },
                      },
                    }}
                    title="Confirmar"
                  />
                </Grid>
              </Grid>
            </>
          ) : (
            <EditIconButton
              onClick={handleEdit}
              type="submit"
              tooltipText={"Editar Detalhes do Alarme"}
            />
          )}
        </Grid>
        {isEditing ? (
          <>
            <Grid
              container
              spacing={2}
              onSubmit={handleSubmit(onSubmit)}
              component={"form"}
            >
              <Grid item xs={12} md={3}>
                <TextInput
                  fieldName={"specializedTechnical"}
                  label={"Técnico Especializado"}
                  control={control}
                  errors={errors}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <TextInput
                  fieldName={"technicalAssistance"}
                  label={"Assistência Técnica"}
                  control={control}
                  errors={errors}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <TextInput
                  fieldName={"provider"}
                  label={"Provedor"}
                  control={control}
                  errors={errors}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <TextInput
                  fieldName={"dealership"}
                  label={"Concessionária"}
                  control={control}
                  errors={errors}
                />
              </Grid>
            </Grid>
          </>
        ) : (
          <>
            <Grid
              display={"flex"}
              container
              justifyContent={"space-between"}
              spacing={4}
            >
              <Grid item xl={3} lg={4} md={3} sm={6} xs={6}>
                <DetailsInfo
                  justifyContent={"start"}
                  removePaddingInMobile
                  paddingLeft
                  title="Técnico Especializado"
                  value={specializedTechnicalEdit ?? "Não informado"}
                />
              </Grid>
              <Grid item xl={3} lg={3} md={3} sm={6} xs={6}>
                <DetailsInfo
                  removePaddingInMobile
                  paddingLeft
                  title="Assistência Técnica"
                  value={technicalAssistanceEdit ?? "Não informado"}
                />
              </Grid>
              <Grid item xl={3} lg={2} md={3} sm={6} xs={6}>
                <DetailsInfo
                  justifyContent={"center"}
                  removePaddingInMobile
                  paddingLeft
                  title="Provedor"
                  value={providerEdit ?? "Não informado"}
                />
              </Grid>
              <Grid item xl={3} lg={3} md={3} sm={6} xs={6}>
                <DetailsInfo
                  removePaddingInMobile
                  paddingLeft
                  title="Concessionária"
                  value={dealershipEdit ?? "Não informado"}
                />
              </Grid>
            </Grid>
          </>
        )}
      </Grid>
      <ConfirmationDialog
        open={isCancelConfirmationOpen}
        title="Descartar alteração"
        message="Deseja descartar e cancelar as alterações?"
        onConfirm={handleConfirmCancel}
        onCancel={() => setCancelConfirmationOpen(false)}
        onClose={() => setCancelConfirmationOpen(false)}
      />
      <ConfirmationModal
        open={isSaveConfirmationOpen}
        setOpen={setSaveConfirmationOpen}
        title="Editar detalhes"
        contentText="Deseja confirmar a edição dos detalhes dos alarmes?"
        onConfirm={handleSubmit(handleConfirmSave)}
        setOpenErrorModal={setOpenErrorModal}
        setOpenSuccessModal={setOpenSuccessModal}
      />

      <ActionAlert
        message="Os detalhes do alarme foram atualizados com sucesso!"
        severity="success"
        open={openSuccessModal}
        setOpen={setOpenSuccessModal}
        onClose={() => {
          onMount();
          setOpenSuccessModal(false);
        }}
      />
      <ActionAlert
        message="Ocorreu um erro ao editar os detalhes do alarme! Tente novamente mais tarde."
        severity="error"
        open={openErrorModal}
        setOpen={setOpenErrorModal}
        onClose={() => {
          setOpenErrorModal(false);
        }}
      />
    </>
  );
}
